import { generateRoomKey, parseKeyParams } from './generateRoomKey';

// The TripDayHotel auto-select effect (useTripDayHotelSelection) restores a
// room selection saved on a draft/template before falling back to the
// cheapest rooms. Both restore paths below share the same 3-way outcome: a
// full match ('matched' — override the selection), a partial/no match
// ('unavailable' — the stay's own rooms are gone, the caller writes an
// unavailable selection and bails out of the effect), or a match that exists
// but no longer fits the trip's current composition ('fallthrough' — the
// caller keeps the cheapest-rooms default, same as if there had been nothing
// to restore at all).

interface RoomMatchResult {
  status: 'matched' | 'unavailable' | 'fallthrough';
  roomsToSelect?: any[];
  roomKeysToSelect?: string;
  missingRoomNames?: string[];
}

type FitsCurrentComposition = (room: any, slotIndex: number) => boolean;

function roomKeysOf(
  rooms: any[],
  buildPossibilities: (index: number, room: any) => any[],
): string {
  return rooms
    .map((room, index) => {
      const currentPossibilities = buildPossibilities(index, room);
      const originalIdx = currentPossibilities.findIndex(
        (p: any) =>
          p.name === room.name &&
          p.bedChoices[0].totalPriceWithPromo ===
            room.bedChoices[0].totalPriceWithPromo,
      );
      return generateRoomKey(
        room,
        originalIdx >= 0 ? originalIdx : 0,
        room?.bed?.id ?? null,
      );
    })
    .join(',');
}

export function matchDraftKeyRooms({
  hotelSelectedRoomKey,
  roomsData,
  fitsCurrentComposition,
}: {
  hotelSelectedRoomKey: string;
  roomsData: any;
  fitsCurrentComposition: FitsCurrentComposition;
}): RoomMatchResult {
  const draftKeys = hotelSelectedRoomKey
    .split(',')
    .map((k) => k.trim())
    .slice(0, roomsData.roomCount);

  const matchedRooms = draftKeys.reduce((acc: any[], key, index) => {
    const { bedId, depth } = parseKeyParams(key);
    // Own inventory has no nested compositions: fall back to the full flat
    // list for slots beyond the first.
    const currentPossibilities =
      index === 0
        ? roomsData.possibilities
        : acc[index - 1]?.compatibleRoomCompositions?.length
          ? acc[index - 1].compatibleRoomCompositions
          : roomsData.possibilities;

    // 1. Exact match using the depth and bedId extracted from the key
    let room = currentPossibilities.find(
      (r: any) => generateRoomKey(r, depth, bedId) === key,
    );

    // 2. Fallback: match by room name
    if (!room) {
      const roomNameFromKey = key.split('_')[0];
      room = currentPossibilities.find((r: any) => r.name === roomNameFromKey);
    }

    if (!room) return acc;

    return [
      ...acc,
      {
        ...room,
        bed:
          room.bedChoices?.find((b: any) => b.id === bedId) ??
          room.bedChoices?.[0],
      },
    ];
  }, []);

  if (
    matchedRooms.length === draftKeys.length &&
    matchedRooms.every((room, index) => fitsCurrentComposition(room, index))
  ) {
    const roomKeysToSelect = roomKeysOf(matchedRooms, (index) =>
      index === 0
        ? roomsData.possibilities
        : matchedRooms[index - 1]?.compatibleRoomCompositions?.length
          ? matchedRooms[index - 1].compatibleRoomCompositions
          : roomsData.possibilities,
    );

    return {
      status: 'matched',
      roomsToSelect: matchedRooms,
      roomKeysToSelect,
    };
  }

  if (matchedRooms.length !== draftKeys.length) {
    // Some rooms are no longer available
    const missingRoomNames = draftKeys.map((key, idx) => {
      if (matchedRooms[idx]) return matchedRooms[idx].name;
      return key.split('_')[0].replace(/-/g, ' ');
    });

    return { status: 'unavailable', missingRoomNames };
  }

  return { status: 'fallthrough' };
}

export function matchPreviousNameRooms({
  previouslySelectedRoomNames,
  roomsData,
  fitsCurrentComposition,
}: {
  previouslySelectedRoomNames: string[];
  roomsData: any;
  fitsCurrentComposition: FitsCurrentComposition;
}): RoomMatchResult {
  const matchedRooms = previouslySelectedRoomNames.reduce(
    (acc: any[], name) => {
      if (acc.length >= roomsData.roomCount) return acc;
      const room = roomsData.possibilities.find((r: any) => r.name === name);
      if (!room) return acc;
      return [...acc, { ...room, bed: room.bedChoices?.[0] }];
    },
    [],
  );

  if (
    matchedRooms.length > 0 &&
    matchedRooms.every((room, index) => fitsCurrentComposition(room, index))
  ) {
    const roomKeysToSelect = roomKeysOf(
      matchedRooms,
      () => roomsData.possibilities,
    );

    return {
      status: 'matched',
      roomsToSelect: matchedRooms,
      roomKeysToSelect,
    };
  }

  if (matchedRooms.length === 0) {
    // Previously selected rooms are no longer available
    const missingRoomNames = previouslySelectedRoomNames.slice(
      0,
      roomsData.roomCount,
    );

    return { status: 'unavailable', missingRoomNames };
  }

  return { status: 'fallthrough' };
}
